import { useEffect, useId, useState } from 'react';
import { HelpLink } from '../help/HelpLink';
import { useSearchParams } from 'react-router-dom';
import {
  categoryLabel,
  DAMAGE_CATEGORIES,
  DAMAGE_STATUSES,
  de,
  statusLabel,
  viewLabel,
  type AircraftView,
  type DamageCategory,
} from '@fsv/core';
import { Chip, Segmented } from '../components/Controls';
import { DamageRow } from '../components/DamageRow';
import { Icon } from '../components/Icon';
import { StatusGlyph } from '../components/Status';
import { useDamageRef, useFleet, useSearch, useTenant } from '../data/hooks';
import type { PeriodDays, SearchQuery } from '@fsv/data';
import { tx } from '@fsv/data';
import { EMPTY_QUERY, exactNumberOf, isActiveQuery, parseSearchParams, toSearchParams } from '@fsv/data';
import { ErrorState, Loading } from './states';
import './SearchScreen.css';

const DEBOUNCE_MS = 250;

export function SearchScreen() {
  const id = useId();
  const [params, setParams] = useSearchParams();
  const query = parseSearchParams(params);
  const [text, setText] = useState(query.q);
  const fleet = useFleet();

  // The URL is the state (shareable); typing is debounced into it.
  useEffect(() => {
    if (text === query.q) return;
    const t = window.setTimeout(() => setParams(toSearchParams({ ...query, q: text }), { replace: true }), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [text, query, setParams]);

  const update = (patch: Partial<SearchQuery>) => setParams(toSearchParams({ ...query, q: text, ...patch }), { replace: true });
  const active = isActiveQuery(query);
  const results = useSearch(query, active);
  const { code } = useTenant();
  const ref = useDamageRef();
  // -1: a reference with another club's code; it finds nothing, and says nothing extra.
  const number = exactNumberOf(query.q, code);
  const facetCount = [query.aircraft, query.category, query.view, query.periodDays].filter((x) => x !== null).length + query.statuses.length + (query.withPhoto ? 1 : 0);

  return (
    <div className="page search">
      <header className="page__head">
        <HelpLink screen="search" className="page__help" />
        <h1>{tx.search.title}</h1>
      </header>
      <form className="search__box" role="search" onSubmit={(e) => e.preventDefault()}>
        <label htmlFor={`${id}-q`} className="sr-only">
          {tx.search.label}
        </label>
        <Icon name="search" />
        <input
          id={`${id}-q`}
          className="search__input"
          data-tour="search-input"
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          placeholder={tx.search.placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {text && (
          <button type="button" className="btn btn--ghost btn--icon" aria-label={tx.search.reset} onClick={() => setText('')}>
            <Icon name="close" />
          </button>
        )}
      </form>

      <details className="facets">
        <summary>
          <Icon name="filter" size={18} />
          {tx.search.filters}
          {facetCount > 0 && <span className="facets__count mono">{facetCount}</span>}
        </summary>
        <div className="facets__body">
          <div className="facets__selects">
            <label className="field">
              <span className="field__label">{tx.detail.aircraft}</span>
              <select className="select" value={query.aircraft ?? ''} onChange={(e) => update({ aircraft: e.target.value || null })}>
                <option value="">{tx.search.anyAircraft}</option>
                {fleet.data?.map((f) => (
                  <option key={f.aircraft.id} value={f.aircraft.registration}>
                    {f.aircraft.registration} · {f.aircraft.typeCode}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">{de.damage.category}</span>
              <select className="select" value={query.category ?? ''} onChange={(e) => update({ category: (e.target.value || null) as DamageCategory | null })}>
                <option value="">{tx.search.anyCategory}</option>
                {DAMAGE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {categoryLabel[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">{tx.detail.view}</span>
              <select className="select" value={query.view ?? ''} onChange={(e) => update({ view: (e.target.value || null) as AircraftView | null })}>
                <option value="">{tx.search.anyView}</option>
                {(Object.keys(viewLabel) as AircraftView[]).map((v) => (
                  <option key={v} value={v}>
                    {viewLabel[v]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="field">
            <span className="field__label">{tx.search.period}</span>
            <Segmented
              label={tx.search.period}
              value={String(query.periodDays ?? 'all')}
              onChange={(v) => update({ periodDays: v === 'all' ? null : (Number(v) as PeriodDays) })}
              options={[
                { value: 'all', label: tx.search.periodAll },
                ...([30, 90, 365] as const).map((d) => ({ value: String(d), label: tx.search.periodDays(d) })),
              ]}
            />
          </div>
          <div className="facets__chips" role="group" aria-label="Status">
            {DAMAGE_STATUSES.map((s) => (
              <Chip key={s} className={`st--${s}`} pressed={query.statuses.includes(s)} onToggle={() => update({ statuses: query.statuses.includes(s) ? query.statuses.filter((x) => x !== s) : [...query.statuses, s] })}>
                <StatusGlyph status={s} size={12} />
                {statusLabel[s]}
              </Chip>
            ))}
            <Chip pressed={query.withPhoto} onToggle={() => update({ withPhoto: !query.withPhoto })}>
              <Icon name="camera" size={16} />
              {tx.search.withPhoto}
            </Chip>
          </div>
          {facetCount > 0 && (
            <button type="button" className="link-btn" onClick={() => setParams(toSearchParams({ ...EMPTY_QUERY, q: text }), { replace: true })}>
              {tx.search.reset}
            </button>
          )}
        </div>
      </details>

      <section aria-live="polite" className="stack">
        {!active ? (
          <p className="muted">{tx.search.minChars}</p>
        ) : results.isPending ? (
          <Loading />
        ) : results.isError ? (
          <ErrorState error={results.error} />
        ) : results.data.length === 0 ? (
          <p className="empty">{tx.search.empty}</p>
        ) : (
          <>
            <p className="label">
              {tx.search.results(results.data.length)}
              {number !== null && number > 0 && ` · ${tx.search.exactNumber(ref(number))}`}
            </p>
            {results.data.map((d) => (
              <DamageRow key={d.id} damage={d} />
            ))}
          </>
        )}
      </section>
    </div>
  );
}
